import { z } from 'zod';
import { uuidV7Schema } from './ids';

export const leadAttributionSchema = z
  .object({
    websiteOrigin: z
      .url({ protocol: /^https$/u })
      .refine((value) => new URL(value).origin === value),
  })
  .strict();
export type LeadAttribution = z.infer<typeof leadAttributionSchema>;

export const leadFiltersSchema = z
  .object({
    from: z.iso.date().optional(),
    through: z.iso.date().optional(),
    before: uuidV7Schema.optional(),
  })
  .strict()
  .refine(
    (value) => !value.from || !value.through || value.from <= value.through,
    'Invalid lead date range.'
  );
export type LeadFilters = z.infer<typeof leadFiltersSchema>;
export const capturedLeadSchema = z.object({
  id: uuidV7Schema,
  conversationId: uuidV7Schema,
  createdAt: z.string(),
  confirmedAt: z.string(),
  channel: z.enum(['web', 'email']),
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string(),
  email: z.string(),
  request: z.string(),
  status: z.enum([
    'pending',
    'executing',
    'accepted',
    'failed',
    'reconciliation_required',
  ]),
  reference: z.string().nullable(),
  acknowledgement: z.enum(['accepted', 'confirmed', 'pending']).nullable(),
});
export type CapturedLead = z.infer<typeof capturedLeadSchema>;
export const leadsPageSchema = z.object({
  leads: z.array(capturedLeadSchema),
  nextCursor: uuidV7Schema.nullable(),
});

// Quoting alone does not prevent spreadsheet formula execution.
export function leadCsvCell(value: string): string {
  const safe = /^[\s]*[=+@-]|^[\t\r\n]/u.test(value) ? `'${value}` : value;
  return `"${safe.replaceAll('"', '""')}"`;
}
export function leadsCsv(leads: readonly CapturedLead[]): string {
  const fields = [
    'id',
    'conversationId',
    'createdAt',
    'confirmedAt',
    'channel',
    'firstName',
    'lastName',
    'phone',
    'email',
    'request',
    'status',
    'reference',
    'acknowledgement',
  ] as const;
  return (
    '\uFEFF' +
    [
      fields.join(','),
      ...leads.map((lead) =>
        fields.map((field) => leadCsvCell(lead[field] ?? '')).join(',')
      ),
    ].join('\r\n') +
    '\r\n'
  );
}
